import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Snackbar,
  Alert,
  Chip,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import HomeIcon from "@mui/icons-material/Home";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PersonIcon from "@mui/icons-material/Person";
import ApiServices from "../../../Services/ApiServices/ApiServices";
import { useNavigate } from "react-router-dom";

const roleOptions = [
  "Owner",
  "Tenant",
  "Landlord",
  "Broker",
  "Property Manager",
  "Family Member",
  "Roommate",
  "Care Taker",
];

const Properties = () => {
  const navigate = useNavigate();

  const getSessionUser = () => {
    try {
      const raw = sessionStorage.getItem("user");
      if (!raw) return null;
      return JSON.parse(raw);
    } catch {
      return null;
    }
  };

  const user = getSessionUser();
  const userId = user?.id ?? null;

  const [properties, setProperties] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState({
    role: "Owner", // default
    is_primary_residence: true,
    move_in_date: "",
    move_out_date: null,
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zipcode: "",
  });

  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  const fetchProps = async () => {
    if (!userId) return;
    try {
      const res = await ApiServices.get(`/properties/by-customer/${userId}/`);
      setProperties(res.data);
      setFiltered(res.data);
    } catch {
      setSnack({ open: true, msg: "Failed to load properties.", severity: "error" });
    }
  };

  useEffect(() => {
    fetchProps();
  }, []);

  useEffect(() => {
    const s = search.toLowerCase();
    const result = properties.filter(
      (p) =>
        p.city.toLowerCase().includes(s) ||
        p.address_line1.toLowerCase().includes(s) ||
        p.zipcode.toLowerCase().includes(s)
    );
    setFiltered(result);
  }, [search, properties]);

  
  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const saveProperty = async () => {
    const payload = { ...form, customer: userId };

    try {
      await ApiServices.post(`/properties/`, payload);
      setSnack({ open: true, msg: "Property added successfully!", severity: "success" });
      setOpenDialog(false);
      fetchProps();
    } catch {
      setSnack({ open: true, msg: "Failed to add property.", severity: "error" });
    }
  };

  return (
    <Box p={3}>
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight={700}>
          My Properties
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          sx={{ borderRadius: 2, px: 3, py: 1.2, fontWeight: 600 }}
        >
          Add Property
        </Button>
      </Box>

      {/* SEARCH */}
      <TextField
        fullWidth
        placeholder="Search by city, address, or zipcode..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3 }}
      />

      {/* PROPERTY CARDS */}
      <Grid container spacing={2}>
        {filtered.length === 0 && (
          <Box width="100%" textAlign="center" mt={5}>
            <Typography color="text.secondary">No properties found.</Typography>
          </Box>
        )}

        {filtered.map((p) => (
          <Grid item xs={12} md={6} lg={4} key={p.property_id}>
            <Card
              onClick={() => navigate(`/customer/property-details/${p.property_id}`)}
              sx={{
                cursor: "pointer",
                borderRadius: 4,
                transition: "0.3s",
                boxShadow: 3,
                "&:hover": { boxShadow: 8, transform: "scale(1.015)" },
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={1} mb={1}>
                  <Chip
                    icon={<PersonIcon />}
                    label={p.role}
                    color="primary"
                    variant="filled"
                    size="small"
                  />
                  {p.is_primary_residence && (
                    <Chip
                      icon={<HomeIcon />}
                      label="Primary Residence"
                      color="success"
                      size="small"
                    />
                  )}
                </Stack>

                <Typography variant="h6" fontWeight={700} gutterBottom>
                  {p.address_line1}
                </Typography>

                {p.address_line2 && (
                  <Typography color="text.secondary" fontSize={14}>
                    {p.address_line2}
                  </Typography>
                )}

                <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                  <LocationOnIcon fontSize="small" color="primary" />
                  <Typography fontSize={14}>
                    {p.city}, {p.state} {p.zipcode}
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={1} mt={1}>
                  <CalendarMonthIcon fontSize="small" color="action" />
                  <Typography fontSize={13} color="text.secondary">
                    Move-in: {p.move_in_date ? p.move_in_date : "N/A"}
                  </Typography>
                </Stack>

                {p.move_out_date && (
                  <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                    <CalendarMonthIcon fontSize="small" color="error" />
                    <Typography fontSize={13} color="text.secondary">
                      Move-out: {p.move_out_date}
                    </Typography>
                  </Stack>
                )}

                <Typography fontSize={12} mt={2} color="text.disabled">
                  Added on: {new Date(p.created_at).toLocaleDateString()}
                </Typography> 
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ADD PROPERTY DIALOG */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add New Property</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <TextField
                select
                label="Select Role"
                name="role"
                value={form.role}
                onChange={handleFormChange}
                fullWidth
                SelectProps={{ MenuProps: { sx: { maxHeight: 300 } } }}
                sx={{ ".MuiSelect-select": { py: 1.5 } }}
              >
                {roleOptions.map((r) => (
                  <MenuItem key={r} value={r}>
                    {r}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={6}>
              <TextField
                type="date"
                label="Move-In Date"
                name="move_in_date"
                value={form.move_in_date}
                onChange={handleFormChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                type="date"
                label="Move-Out Date"
                name="move_out_date"
                value={form.move_out_date}
                onChange={handleFormChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address Line 1"
                name="address_line1"
                value={form.address_line1}
                onChange={handleFormChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Address Line 2"
                name="address_line2"
                value={form.address_line2}
                onChange={handleFormChange}
                fullWidth
              />
            </Grid>

            <Grid item xs={6}>
              <TextField label="City" name="city" value={form.city} onChange={handleFormChange} fullWidth />
            </Grid>

            <Grid item xs={3}>
              <TextField label="State" name="state" value={form.state} onChange={handleFormChange} fullWidth />
            </Grid>

            <Grid item xs={3}>
              <TextField label="Zipcode" name="zipcode" value={form.zipcode} onChange={handleFormChange} fullWidth />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveProperty} sx={{ px: 3 }}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* SNACKBAR */}
      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.severity} variant="filled">
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Properties;


